import React, { useMemo } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { FeatureRow } from '~/pages/Flags/FeatureRow';
import { styled } from '@customink/pigment-react/lib/themeCustomink';
import { useOptimizely } from '~/contexts/OptimizelyContext';
import { ButtonLink } from '~/components/Link/Link';
import { DesignsPath } from '~/config';
import { Button, LinkIcon, RefreshIcon, Stack } from '@customink/pigment-react';
import { LinkInBlank } from '~/components/Link/LinkInBlank';

const HeadCell = styled(TableCell)({
    fontWeight: 'bold',
});

const useOptimizelyProjectId = () => {
    const { optimizely } = useOptimizely();
    const optimizelyConfig = optimizely.getOptimizelyConfig();
    if (optimizelyConfig) {
        // the recommended way to get projectId https://docs.developers.optimizely.com/feature-experimentation/docs/react-quickstart#1-add-event-tracking
        return JSON.parse(optimizelyConfig.getDatafile()).projectId;
    }
    // hardcoded fallback (the accounts-ui projectId)
    return 22136960345;
};

export const FlagsTable: React.FC = () => {
    const { optimizely } = useOptimizely();
    const featuresMap = optimizely.getOptimizelyConfig()?.featuresMap;
    const features = useMemo(
        () => Object.values(featuresMap ?? {}).sort((a, b) => a.key.localeCompare(b.key)),
        [featuresMap],
    );
    const { resetAllFeatureFlags } = useOptimizely();

    const projectId = useOptimizelyProjectId();
    const optimizelyLink = `https://app.optimizely.com/v2/projects/${projectId}/flags/list`;

    return (
        <>
            <Stack justifyContent="flex-end" direction="row" spacing={2} my={2}>
                <ButtonLink to={DesignsPath} style={{ marginRight: 'auto' }}>
                    Safely Go Back
                </ButtonLink>
                <Button variant="secondary" href={optimizelyLink} LinkComponent={LinkInBlank} startIcon={<LinkIcon />}>
                    Optimizely app
                </Button>
                <Button variant="secondary" onClick={resetAllFeatureFlags} startIcon={<RefreshIcon />}>
                    Reset all flags
                </Button>
            </Stack>

            <TableContainer>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            <HeadCell>Feature</HeadCell>
                            <HeadCell>Available Flags</HeadCell>
                            <HeadCell>Your flag</HeadCell>
                            <HeadCell>Actions</HeadCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {features.map((feature) => (
                            <FeatureRow feature={feature} key={feature.id} />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
};
